import { Instance, SnapshotOut, types } from "mobx-state-tree"

import { withEnvironment } from "../extensions/with-environment";
import {
  IMessage,
  withRootStore,
} from "../../models";
import {
  TGeneralApiResponse,
  UserApi
} from "../../services";
import { createUserEmailCert, IUserEmailCert } from '../user-email-cert/UserEmailCert';
import { createUser, IUser } from '../user/User';
import { nowSec, setSession, tokenDestroy } from "src/auth/util";
import { withSetPropAction } from "../extensions/withSetPropAction";

/**
 * # UserStore
 * 
 * UserStore을 설명하세요.
 */

export const UserStore = types
  .model("UserStore")
  // --------------------------------------------------------------------------
  .props({
    loginId: types.maybeNull(types.optional(types.string, '')),
    userEmailCert: createUserEmailCert(),
    user: createUser(),
    isDuplicated: types.maybeNull(types.optional(types.boolean, false)),
  })
  .extend(withEnvironment)
  .extend(withRootStore)
  .actions(withSetPropAction)
  // eslint-disable-line @typescript-eslint/no-unused-vars
  .views((self) => ({}))
  // --------------------------------------------------------------------------
  // MUTATEs - 모델 상태를 변경
  .actions((self) => ({
    /**
     * users을 교체
     *
     * @param `users` 새로운 모델의 배열
     */
    setLoginId: (loginId: string | null) => {
      self.loginId = loginId;
    },

    setUserEmailCert: (userEmailCert: IUserEmailCert) => {
      self.userEmailCert = userEmailCert;
    },

    setUser: (user: IUser) => {
      self.user = user;
    },

    setIsDuplicated: (isDuplicated: boolean) => {
      self.isDuplicated = isDuplicated;
    },
  }))
  // --------------------------------------------------------------------------
  // REQUESTs - 서비스 요청 및 기타 인터페이스 요청 
  .actions((self) => ({
    /**
     * 전체 목록을 Api를 통해 조회
     * 
     * 조회한 결과로 Users를 교체한다. 실패시 에러 로그를 남긴다.
     */

    /** 아이디 중복 체크 */
    checkLoginIdDuplicate: async (loginId: string) => {
      self.setIsDuplicated(false);
      self.rootStore.messageStore.setMessage({} as IMessage);

      try {
        const userApi: UserApi = new UserApi(self.environment.api);
        const result: TGeneralApiResponse = await userApi.checkLoginIdDuplicate(loginId);

        if (self.rootStore.responseStore.getResponseResult(result)) {
          if (self.rootStore.responseStore.responseInfo.resultCode == 'S') {
            const isDuplicated = JSON.parse(JSON.stringify(result)).responseInfo.data.isDuplicated;
            self.setIsDuplicated(isDuplicated);
          }
        }
      } catch (e) {
        self.rootStore.responseStore.errorProcessing(e);
      }
    },

    /** 회원가입 */
    register: async (user: IUser) => {
      self.rootStore.messageStore.setMessage({} as IMessage);

      try {
        const userApi: UserApi = new UserApi(self.environment.api);
        const result: TGeneralApiResponse = await userApi.register(user);

        if (self.rootStore.responseStore.getResponseResult(result)) {
          if (self.rootStore.responseStore.responseInfo.resultCode == 'S') {
            self.rootStore.messageStore.setMessage({
              content: JSON.parse(JSON.stringify(result)).responseInfo.data,
              isError: false,
            } as IMessage);
          }
        }
      } catch (e) {
        self.rootStore.responseStore.errorProcessing(e);
      }
    },

    /** 로그인 */
    login: async (user: IUser) => {
      self.rootStore.messageStore.setMessage({} as IMessage);

      try {
        const userApi: UserApi = new UserApi(self.environment.api);
        const result: TGeneralApiResponse = await userApi.login(user);

        if (self.rootStore.responseStore.getResponseResult(result)) {
          if (self.rootStore.responseStore.responseInfo.resultCode == 'S') {
            const data = JSON.parse(JSON.stringify(result)).responseInfo.data;

            const token = JSON.parse(data.token);
            const expiresAt = nowSec() + token.expires_in;
            setSession(token.access_token, token.refresh_token, expiresAt);

            const user = data.user;
            self.setUser(user);

          } else {
            tokenDestroy();
            self.setUser({} as IUser);
            if (self.rootStore.responseStore.responseInfo.message) {
              self.rootStore.messageStore.setMessage({
                content: self.rootStore.responseStore.responseInfo.message,
                isError: true,
              } as IMessage);
            }
          }
        }
      } catch (e) {
        self.rootStore.responseStore.errorProcessing(e);
      }
    },

    /** 로그아웃 */
    logout: async () => {
      self.rootStore.messageStore.setMessage({} as IMessage);

      try {
        const userApi: UserApi = new UserApi(self.environment.api);
        const result: TGeneralApiResponse = await userApi.logout();

        if (self.rootStore.responseStore.getResponseResult(result)) {
          if (self.rootStore.responseStore.responseInfo.resultCode == 'S') {
            tokenDestroy();
            self.setUser({} as IUser);
          } else {
            if (self.rootStore.responseStore.responseInfo.message) {
              self.rootStore.messageStore.setMessage({
                content: self.rootStore.responseStore.responseInfo.message,
                isError: true,
              } as IMessage);
            }
          }
        }
      } catch (e) {
        self.rootStore.responseStore.errorProcessing(e);
      }
    },

    /** 아이디 찾기 */
    findLoginId: async (user: IUser) => {
      self.setLoginId(null);

      try {
        const userApi: UserApi = new UserApi(self.environment.api);
        const result: TGeneralApiResponse = await userApi.findLoginId(user);

        if (self.rootStore.responseStore.getResponseResult(result)) {
          if (self.rootStore.responseStore.responseInfo.resultCode == 'S') {
            const loginId = JSON.parse(JSON.stringify(result)).responseInfo.data.loginId;
            self.setLoginId(loginId);
          }
        }
      } catch (e) {
        self.rootStore.responseStore.errorProcessing(e);
      }
    },

    /** 비밀번호 재설정 - 계정 조회 */
    resetPwdFindAccount: async (user: IUser) => {
      self.setUserEmailCert({} as IUserEmailCert);

      try {
        const userApi: UserApi = new UserApi(self.environment.api);
        const result: TGeneralApiResponse = await userApi.resetPwdFindAccount(user);

        if (self.rootStore.responseStore.getResponseResult(result)) {
          if (self.rootStore.responseStore.responseInfo.resultCode == 'S') {
            const userEmailCert = JSON.parse(JSON.stringify(result)).responseInfo.data.userEmailCert;
            if (userEmailCert) {
              self.setUserEmailCert(userEmailCert);
            }
          }
        }
      } catch (e) {
        self.rootStore.responseStore.errorProcessing(e);
      }
    },

    /** 비밀번호 재설정 - 이메일 인증 */
    resetPwdCertifyEmail: async (userEmailCert: IUserEmailCert) => {
      try {
        const userApi: UserApi = new UserApi(self.environment.api);
        const result: TGeneralApiResponse = await userApi.resetPwdCertifyEmail(userEmailCert);

        if (self.rootStore.responseStore.getResponseResult(result)) {
          if (self.rootStore.responseStore.responseInfo.resultCode == 'S') {
            const userEmailCert = JSON.parse(JSON.stringify(result)).responseInfo.data;
            self.setUserEmailCert(userEmailCert as IUserEmailCert);
          }
        }
      } catch (e) {
        self.rootStore.responseStore.errorProcessing(e);
      }
    },

    /** 비밀번호 재설정 */
    resetPwd: async (user: IUser) => {
      try {
        const userApi: UserApi = new UserApi(self.environment.api);
        const result: TGeneralApiResponse = await userApi.resetPwd(user);
        self.rootStore.responseStore.getResponseResult(result);
      } catch (e) {
        self.rootStore.responseStore.errorProcessing(e);
      }
    },

    /** 회원정보 조회 */
    getProfile: async () => {
      self.rootStore.messageStore.setMessage({} as IMessage);

      try {
        const userApi: UserApi = new UserApi(self.environment.api);
        const result: TGeneralApiResponse = await userApi.getProfile();
        if (self.rootStore.responseStore.getResponseResult(result)) {
          if (self.rootStore.responseStore.responseInfo.resultCode == 'S') {
            const user = JSON.parse(JSON.stringify(result)).responseInfo.data;
            self.setUser(user);
          } else {
            if (self.rootStore.responseStore.responseInfo.message) {
              self.rootStore.messageStore.setMessage({
                content: self.rootStore.responseStore.responseInfo.message,
                isError: true,
              } as IMessage);
            }
          }
        }
      } catch (e) {
        self.rootStore.responseStore.errorProcessing(e);
      }
    },

    /** 회원정보 수정 */
    updateProfile: async (user: IUser) => {
      self.rootStore.messageStore.setMessage({} as IMessage);

      try {
        const userApi: UserApi = new UserApi(self.environment.api);
        const result: TGeneralApiResponse = await userApi.updateProfile(user);
        if (self.rootStore.responseStore.getResponseResult(result)) {
          if (self.rootStore.responseStore.responseInfo.resultCode == 'S') {
            const user = JSON.parse(JSON.stringify(result)).responseInfo.data;
            self.setUser(user);
            self.rootStore.messageStore.setMessage({
              content: '회원정보가 정상적으로 수정되었습니다.',
              isError: false,
            } as IMessage);
          } else {
            if (self.rootStore.responseStore.responseInfo.message) {
              self.rootStore.messageStore.setMessage({
                content: self.rootStore.responseStore.responseInfo.message,
                isError: true,
              } as IMessage);
            }
          }
        }
      } catch (e) {
        self.rootStore.responseStore.errorProcessing(e);
      }
    },

    /** 회원정보 수정 - 비밀번호 재설정 */
    resetPwdByProfile: async (user: IUser) => {
      self.rootStore.messageStore.setMessage({} as IMessage);

      try {
        const userApi: UserApi = new UserApi(self.environment.api);
        const result: TGeneralApiResponse = await userApi.resetPwdByProfile(user);
        if (self.rootStore.responseStore.getResponseResult(result)) {
          if (self.rootStore.responseStore.responseInfo.resultCode == 'S') {
            const user = JSON.parse(JSON.stringify(result)).responseInfo.data;
            self.setUser(user);
            self.rootStore.messageStore.setMessage({
              content: '비밀번호가 재설정되었습니다.',
              isError: false,
            } as IMessage);
          } else {
            if (self.rootStore.responseStore.responseInfo.message) {
              self.rootStore.messageStore.setMessage({
                content: self.rootStore.responseStore.responseInfo.message,
                isError: true,
              } as IMessage);
            }
          }
        }
      } catch (e) {
        self.rootStore.responseStore.errorProcessing(e);
      }
    },

    /** 회원탈퇴 */
    withdrawal: async () => {
      self.rootStore.messageStore.setMessage({} as IMessage);

      try {
        const userApi: UserApi = new UserApi(self.environment.api);
        const result: TGeneralApiResponse = await userApi.withdrawal();

        if (self.rootStore.responseStore.getResponseResult(result)) {
          if (self.rootStore.responseStore.responseInfo.resultCode == 'S') {
            tokenDestroy();
            self.setUser({} as IUser);
            self.rootStore.messageStore.setMessage({
              content: JSON.parse(JSON.stringify(result)).responseInfo.data,
              isError: false,
            } as IMessage);
          } else {
            if (self.rootStore.responseStore.responseInfo.message) {
              self.rootStore.messageStore.setMessage({
                content: self.rootStore.responseStore.responseInfo.message,
                isError: true,
              } as IMessage);
            }
          }
        }
      } catch (e) {
        self.rootStore.responseStore.errorProcessing(e);
      }
    },
  }))

// --------------------------------------------------------------------------
type TUserStore = Instance<typeof UserStore>;
type TUserStoreSnapshot = SnapshotOut<typeof UserStore>

export interface IUserStore extends TUserStore { }
export type TUserStoreKeys = keyof TUserStoreSnapshot & string;
export interface IUserStoreSnapshot extends TUserStoreSnapshot { }
export const createUserStore = () =>
  types.optional(UserStore, {} as TUserStore);
